import { Component } from "react"
import { toast } from "react-toastify"

import { APP_PAGE_URL } from "../../../utils/env-management"
import * as utils from "../../../utils/utils"
import { Spinner } from "../../../components/Spinner"
import { AskUserModal } from "../../../components/AskUserModal"
import { ServiceType } from "../../../utils/helpers"
import { bookSession } from "../../BookingCommon/common/api"
import { httpClient } from "../../../utils/infrastructure/httpClient"
import { Button } from "../../../ui-kit/components-branded/index"

import { LocationSelect } from "./LocationSelect"
import { BookingCalendar } from "./BookingCalendar"

export function checkRefundPossibilityApi(bookingId) {
    return httpClient.get(`booking-refund-is-possible/${bookingId}`)
}

function bookingPaymentUrl({ fromMode = "unknown", psyHash, bookingHash }) {
    return `${APP_PAGE_URL}/payment/${bookingHash}?from_mode=${fromMode}&hash_id=${psyHash}`
}

const statuses = {
    loading: "loading",
    button: "button",
    location: "location",
    calendar: "calendar",
    success: "success",
    error: "error",
    impossible: "impossible",
}

function httpErrorNotification() {
    toast.error("Ошибка сервера, попробуйте перезагрузить страницу")
}

const modals = {
    move: "move",
    moveWithoutRefund: "moveWithoutRefund",
    none: "none",
}

export class BookingMove extends Component {
    state = {
        status: statuses.loading,
        location: "online",
        newDate: null,
        bookingRefundIsPossible: null,
        openedModal: modals.none,
        selectedDate: null,
        selectedSlotId: null,
    }

    componentDidMount() {
        this.checkRefundPossibility()
            .then(() => {
                this.setState({
                    status: this.movingIsImpossible() ? statuses.impossible : statuses.button,
                })
            })
            .catch(this.errorHandler)
    }

    movingIsImpossible() {
        return this.props.booking.is_free && this.state.bookingRefundIsPossible === false
    }

    errorHandler = () => {
        this.setState({ status: statuses.error })
        httpErrorNotification()
    }

    checkRefundPossibility() {
        return checkRefundPossibilityApi(this.props.booking.booking_id)
            .then((response) => {
                this.setState({
                    bookingRefundIsPossible: response.booking_refund_is_possible,
                })
                this.props.onRefundCheck(response.booking_refund_is_possible)
                return response
            })
            .catch((e) => {
                this.errorHandler()
                this.setState({ bookingRefundIsPossible: null })
                throw e
            })
    }

    handleLocationSelectChange = (offeredService) => {
        this.setState({
            status: statuses.calendar,
            serviceType: new ServiceType(offeredService),
        })
    }

    moveButtonClick = () => {
        this.setState({
            status: this.isRefundPossible() ? statuses.calendar : statuses.location,
        })
    }

    closeModal = () => {
        this.setState({ openedModal: modals.none, selectedDate: null })
    }

    isRefundPossible() {
        return this.state.bookingRefundIsPossible === null || this.state.bookingRefundIsPossible
    }

    buttonText() {
        return this.isRefundPossible() || this.props.booking.price === 0
            ? "Перенести сессию"
            : "Перенести сессию без возврата денег"
    }

    handleHourSelected = (date, slotId) => {
        void this.checkRefundPossibility().then(({ booking_refund_is_possible }) => {
            if (this.movingIsImpossible()) {
                this.setState({ status: statuses.impossible })
                return
            }
            const openedModal = booking_refund_is_possible ? modals.move : modals.moveWithoutRefund

            this.setState({
                openedModal,
                selectedDate: date,
                selectedSlotId: slotId,
            })
        })
    }

    acceptMove = () => {
        const { openedModal, selectedDate, selectedSlotId } = this.state

        if (openedModal !== modals.move || !selectedDate) {
            return
        }

        void this.checkRefundPossibility().then(({ booking_refund_is_possible }) => {
            if (this.movingIsImpossible()) {
                this.setState({ status: statuses.impossible })
                return
            }

            if (booking_refund_is_possible) {
                this.moveBooking(selectedDate, selectedSlotId)
            } else {
                this.setState({ openedModal: modals.moveWithoutRefund })
            }
        })
        this.closeModal()
    }

    acceptMoveWithoutRefund = () => {
        const { openedModal, selectedDate, selectedSlotId } = this.state

        if (openedModal !== modals.moveWithoutRefund || !selectedDate) {
            return
        }

        this.props
            .cancelBooking()
            .then(() => {
                this.createNewBooking(selectedDate, selectedSlotId)
                    .then(({ bookings: [bookingId] }) => {
                        this.redirectToPayment(bookingId.hash)
                    })
                    .catch(this.errorHandler)
            })
            .catch(this.errorHandler)
        this.closeModal()
    }

    redirectToPayment(bookingHash) {
        window.location.replace(
            bookingPaymentUrl({
                bookingHash,
                psyHash: this.props.booking.psy.hash_id,
                fromMode: "manual",
            })
        )
    }

    moveBooking(date, slotId) {
        const data = {
            date: utils.formatUtcDateIso(date),
            time: utils.formatUtcTimeIso(date),
            booking_id: this.props.booking.booking_id,
            client_data: {
                name: this.props.booking.client_name,
                email: this.props.booking.client_email,
                phone: this.props.booking.client_phone,
            },
            slot_id: slotId,
        }

        httpClient
            .post("move-booking-by-client", data)
            .then(() => {
                this.setState({ status: statuses.success, newDate: date })
                toast("Сессия успешно перенесена")
            })
            .catch(this.errorHandler)
    }

    createNewBooking(sessionDatetime, selectedSlotId) {
        return bookSession({
            sessionDatetime,
            slot_id: selectedSlotId,
            client_data: {
                name: this.props.booking.client_name,
                email: this.props.booking.client_email,
                phone: this.props.booking.client_phone,
            },
            psy_hash_id: this.props.booking.psy.hash_id,
            service_type: this.state.serviceType || this.props.booking.service_type,
            moving: true,
            created_by_psychologist: false,
        })
    }

    renderSuccess() {
        const { newDate } = this.state

        if (!newDate) {
            return
        }

        return (
            <div
                className="alert alert-success font-weight-normal font-size-4 p-3 mt-4"
                role="alert"
            >
                Сессия перенесена на{" "}
                <strong>
                    {utils.formatLocalDate(newDate)} {utils.formatLocalTime(newDate)}
                </strong>
            </div>
        )
    }

    render() {
        return (
            <div className="booking-ui">
                <AskUserModal.View
                    isOpen={this.state.openedModal === modals.moveWithoutRefund}
                    onClose={this.closeModal}
                    onAccept={this.acceptMoveWithoutRefund}
                >
                    При переносе сессии менее чем за 24 часа до начала требуется повторная оплата.{" "}
                    {this.props.booking.discounted !== null &&
                        "Скидка будет считаться использованной. "}
                    Это действие нельзя отменить. Продолжить?
                    <br />
                </AskUserModal.View>

                <AskUserModal.View
                    isOpen={this.state.openedModal === modals.move}
                    onClose={this.closeModal}
                    onAccept={this.acceptMove}
                >
                    Вы уверены?
                    <br />
                </AskUserModal.View>

                {(() => {
                    switch (this.state.status) {
                        case statuses.loading:
                            return <Spinner />
                        case statuses.button:
                            return (
                                <div className="book-button-container">
                                    <Button color="primary" onClick={this.moveButtonClick}>
                                        {this.buttonText()}
                                    </Button>
                                </div>
                            )
                        case statuses.location:
                            return (
                                <LocationSelect
                                    psy={this.props.booking.psy}
                                    showPrice={this.props.showPrice}
                                    onChange={this.handleLocationSelectChange}
                                />
                            )
                        case statuses.calendar:
                            return (
                                <div className="booking-widget">
                                    <BookingCalendar
                                        psy={this.props.booking.psy}
                                        client={this.props.booking.client}
                                        serviceType={
                                            this.state.serviceType ||
                                            this.props.booking.service_type
                                        }
                                        onHourSelected={this.handleHourSelected}
                                        source={{ mode: "profile", search_terms: {} }}
                                        for_psychologist={false}
                                    />
                                </div>
                            )
                        case statuses.success:
                            return this.renderSuccess()
                        case statuses.error:
                            return (
                                <div className="error-message alert-danger font-size-4 p-3 mt-4">
                                    Произошла ошибка. Попробуйте перезагрузить страницу
                                </div>
                            )
                        case statuses.impossible:
                        default:
                            return null
                    }
                })()}
            </div>
        )
    }
}
